import { useEffect, useState } from "react";
import Pagination from "./Pagination";
import {
  ApproveWalletProvider,
  getProviderWalletReport,
} from "../../../config/LiveApi";
import { useSelector } from "react-redux";
import UploadBalance from "./UploadBalance";
import { FcApprove, FcDisapprove } from "react-icons/fc";
import Modal from "./Modal";
import Spinner from "../../Spin/Spinner";
import { toast } from "react-toastify";
import DataTable from "../../../components/common/DataTable/DataTable";
import DateCell from "../../../components/common/TableCells/DateCell";
import StatusCell from "../../../components/common/TableCells/StatusCell";

const ProviderWalletTable = () => {
  const token = useSelector((store) => store.userReducer.token);

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [tableData, setTableData] = useState({ data: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const [modalData, setModalData] = useState({
    isOpen: false,
    id: null,
    status: "",
  });

  const getProviderWalletData = async () => {
    setLoading(true);
    try {
      const response = await getProviderWalletReport(token, currentPage);
      setTableData(response.data);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setActionLoading(true);
    try {
      await ApproveWalletProvider(token, {
        id: modalData.id,
        status: modalData.status,
      });

      toast.success(`Wallet ${modalData.status.toLowerCase()} successfully`);
      setModalData({ isOpen: false, id: null, status: "" });
      getProviderWalletData();
    } catch (err) {
      toast.error("Failed to update wallet status");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = () => {
    if (actionLoading) return;
    setModalData({ isOpen: false, id: null, status: "" });
  };

  useEffect(() => {
    if (token) getProviderWalletData();
  }, [currentPage, token]);

  const columns = [
    {
      header: "Provider Name",
      accessor: "pg",
      render: (row) => row.pg || "-",
    },
    {
      header: "Org Name",
      accessor: "orgName",
      render: (row) => row.orgName || "-",
    },
    {
      header: "Upload Amount",
      accessor: "uploadAmount",
      render: (row) => Number(row.uploadAmount || 0).toFixed(2),
    },
    {
      header: "Provider API Balance",
      accessor: "providerBalance",
      render: (row) => Number(row.providerBalance || 0).toFixed(2),
    },
    {
      header: "Status",
      accessor: "status",
      render: (row) => <StatusCell status={row.status} />,
    },
    {
      header: "Upload By",
      accessor: "createdBy",
      render: (row) => row.createdBy || "-",
    },
    {
      header: "Approved By",
      accessor: "approvedBy",
      render: (row) => row.approvedBy || "N/A",
    },
    {
      header: "Created At",
      accessor: "createdAt",
      render: (row) => <DateCell date={row.createdAt} />,
    },
    {
      header: "Action",
      accessor: "action",
      render: (row) =>
        row.status === "PENDING" ? (
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              className="approve-btn"
              disabled={actionLoading}
              onClick={() =>
                setModalData({
                  isOpen: true,
                  id: row.id,
                  status: "APPROVED",
                })
              }
              title="Approve"
            >
              <FcApprove size={28} />
            </button>

            <button
              className="approve-btn"
              disabled={actionLoading}
              onClick={() =>
                setModalData({
                  isOpen: true,
                  id: row.id,
                  status: "REJECTED",
                })
              }
              title="Reject"
            >
              <FcDisapprove size={28} />
            </button>
          </div>
        ) : (
          "Already Approved"
        ),
    },
  ];

  return (
    <div className="settlement-report-container">
      <div className="settlement-report-excel">
        <div className="settlement-report-title">Provider Wallet Report</div>
        <button
          className="report-add-button"
          onClick={() => setIsUploadOpen(true)}
          disabled={loading || actionLoading}
        >
          Upload Balance
        </button>
      </div>

      <div className="settlement-report-table-wrapper">
        {loading ? (
          <Spinner />
        ) : (
          <DataTable
            columns={columns}
            data={tableData.data || []}
            emptyMessage="No Data Found"
          />
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />

      {isUploadOpen && (
        <UploadBalance
          onClose={() => setIsUploadOpen(false)}
          onSuccess={getProviderWalletData}
        />
      )}

      {modalData.isOpen && (
        <Modal
          message={`Are you sure you want to mark this Wallet as ${modalData.status}?`}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          loading={actionLoading}
        />
      )}

      {actionLoading && (
        <div className="spinner-overlay">
          <Spinner />
        </div>
      )}
    </div>
  );
};

export default ProviderWalletTable;
